import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Portfolio.css';

import { getProjectsByLanguage } from '../../../Data/portfolioDataMultiLang';

const Portfolio = () => {
  const { t } = useTranslation();
  const projects = getProjectsByLanguage('en');

  return (
    <section id="portfolio" className="portfolio">
      <div className="container">
        {/* Section Header */}
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="section-tag">{t('portfolio.tag')}</div>
          <h2>
            {t('portfolio.title')}{' '}
            <span className="gradient-text">{t('portfolio.titleHighlight')}</span>
          </h2>
          <p>{t('portfolio.subtitle')}</p>
        </motion.div>

        {/* Clients Circle Grid */}
        <motion.div
          className="clients-grid"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {projects.map((project, index) => (
            <motion.a
              key={project.id}
              href={project.link !== '#' ? project.link : undefined}
              target={project.link !== '#' ? '_blank' : undefined}
              rel={project.link !== '#' ? 'noopener noreferrer' : undefined}
              className="client-circle"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label={`${project.client} - ${project.title}`}
              onClick={project.link === '#' ? (e) => e.preventDefault() : undefined}
            >
              <div className="client-circle-image">
                <img
                  src={project.image}
                  alt={project.client}
                  loading="lazy"
                />
              </div>
              <span className="client-circle-name">{project.client}</span>
            </motion.a>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default Portfolio;

/* ========== OLD PORTFOLIO SECTION (COMMENTED OUT) ==========

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Portfolio.css';
import PortfolioService from '../../../services/portfolioService';

const PortfolioOld = () => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);

  const currentLanguage = i18n.language;
  const isRTL = currentLanguage === 'ar';

  useEffect(() => {
    const categoriesList = PortfolioService.getCategoriesWithNames(currentLanguage);
    setCategories(categoriesList);
  }, [currentLanguage]);

 useEffect(() => {
  if (selectedProject) {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.dataset.scrollY = scrollY;
  } else {
    const scrollY = document.body.dataset.scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY, 10));
    }
  }
}, [selectedProject]);

  useEffect(() => {
    const filteredProjects = PortfolioService.getProjectsByCategory(activeCategory, currentLanguage);
    setProjects(filteredProjects);
  }, [activeCategory, currentLanguage]);

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && selectedProject) handleCloseModal();
    };
    const handleClickOutside = (event) => {
      if (selectedProject && event.target.classList.contains('project-modal-overlay')) handleCloseModal();
    };
    if (selectedProject) {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedProject]);

  const renderProjectCategories = (project) => {
    if (!project.categories || !Array.isArray(project.categories) || project.categories.length === 0) {
      return <div className="project-category">No categories</div>;
    }
    const categoryArray = PortfolioService.getProjectCategoriesArray(project, currentLanguage);
    return (
      <div className="project-categories">
        {categoryArray.map((category, index) => (
          <React.Fragment key={category.id}>
            <span className="project-category-badge">{category.name}</span>
            {index < categoryArray.length - 1 && <span className="category-separator">, </span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <section id="portfolio" className="portfolio">
      <div className="container">
        <motion.div className="section-header" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <div className="section-tag">{t('portfolio.tag')}</div>
          <h2>{t('portfolio.title')} <span className="gradient-text">{t('portfolio.titleHighlight')}</span></h2>
          <p>{t('portfolio.subtitle')}</p>
        </motion.div>

        <motion.div className="portfolio-filters" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} dir={isRTL ? 'rtl' : 'ltr'}>
          {categories.map((category) => (
            <motion.button key={category.id} className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`} onClick={() => setActiveCategory(category.id)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              {category.displayName}
            </motion.button>
          ))}
        </motion.div>

        <motion.div className="portfolio-grid" layout>
          <AnimatePresence mode="wait">
            {projects.map((project, index) => (
              <motion.div key={project.id} className={`portfolio-card ${project.featured ? 'featured' : ''}`} layout initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.5, delay: index * 0.1 }} whileHover={{ y: -10 }} onClick={() => setSelectedProject(project)}>
                <div className="project-image">
                  <img src={project.image} alt={project.title} loading="lazy" />
                  <div className="project-overlay">
                    <motion.div className="view-project" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                      {t('portfolio.viewProject')} {isRTL ? '←' : '→'}
                    </motion.div>
                  </div>
                  {project.featured && <div className="featured-badge">{t('portfolio.featured')}</div>}
                </div>
                <div className="project-info">
                  {renderProjectCategories(project)}
                  <h3>{project.title}</h3>
                  <p className="project-client">
                    <span className="client-label">{t('portfolio.client')}:</span> {project.client}
                    <span className="separator"> • </span>
                    <span className="year-label">{t('portfolio.year')}:</span> {project.year}
                  </p>
                  <div className="project-tags">
                    {project.tags && project.tags.slice(0, 3).map((tag, i) => <span key={i} className="tag">{tag}</span>)}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {selectedProject && (
            <motion.div className="project-modal-overlay" onClick={handleCloseModal}>
              <motion.div className="project-modal" onClick={(e) => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'}>
                <button className="modal-close" onClick={handleCloseModal}>×</button>
                <div className="modal-content">
                  <div className="modal-image"><img src={selectedProject.image} alt={selectedProject.title} /></div>
                  <div className="modal-details">
                    <h2>{selectedProject.title}</h2>
                    <div className="modal-meta">
                      <div className="meta-item"><strong>{t('portfolio.client')}:</strong> {selectedProject.client}</div>
                      <div className="meta-item"><strong>{t('portfolio.year')}:</strong> {selectedProject.year}</div>
                    </div>
                    <p className="modal-description">{selectedProject.description}</p>
                    <div className="modal-tags">{selectedProject.tags && selectedProject.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}</div>
                    {selectedProject.link && selectedProject.link !== '#' && (
                      <motion.a href={selectedProject.link} className="btn-primary" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        {t('portfolio.viewLive')} {isRTL ? '←' : '→'}
                      </motion.a>
                    )}
                  </div>
                </div>
                <div className="modal-close-bar"><button className="modal-close-btn" onClick={handleCloseModal}>{t('portfolio.close') || 'Close'}</button></div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

========== END OLD PORTFOLIO SECTION ========== */